import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Brand } from "@/components/Brand";

function Shell({ title, subtitle, children, testId }) {
  return (
    <div className="min-h-screen bg-black text-white grain">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-10">
        <Brand dark subtitle="Team dashboard" />
        <div className="mt-8 rounded-2xl border-2 border-[#FACC15] bg-white p-6 text-black sm:p-8 fade-up" style={{ boxShadow: "6px 6px 0 0 #FACC15" }} data-testid={testId}>
          <h1 className="font-display text-2xl font-bold">{title}</h1>
          <p className="mt-1 text-sm text-zinc-600">{subtitle}</p>
          <div className="mt-6 space-y-5">{children}</div>
        </div>
        <Link to="/team-login" className="mt-6 text-center text-sm font-bold text-[#FACC15] underline" data-testid="back-to-login-link">Back to login</Link>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(data.message);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Forgot your password?" subtitle="Enter your team email and we'll send you a reset link." testId="forgot-form">
      {sent ? (
        <p className="rounded-lg border-2 border-black bg-[#FEF9C3] px-3 py-3 text-sm font-semibold" data-testid="forgot-sent">{sent}</p>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="field-label">Email</label>
            <input className="field-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" data-testid="forgot-email-input" />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={busy} data-testid="forgot-submit-button">{busy ? "Sending…" : "Send reset link"}</button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated — please sign in");
      navigate("/team-login", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Choose a new password" subtitle="Minimum 8 characters. The link works once and expires after 1 hour." testId="reset-form">
      {!token ? (
        <p className="rounded-lg border-2 border-[#DC2626] bg-[#FEF2F2] px-3 py-2 text-sm font-semibold text-[#B91C1C]" data-testid="reset-no-token">This link is missing its token. Open the link from your email again or request a new one.</p>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="field-label">New password</label>
            <input className="field-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} minLength={8} required autoComplete="new-password" data-testid="reset-password-input" />
          </div>
          <div>
            <label className="field-label">Repeat new password</label>
            <input className="field-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" data-testid="reset-confirm-input" />
          </div>
          {error && <p className="rounded-lg border-2 border-[#DC2626] bg-[#FEF2F2] px-3 py-2 text-sm font-semibold text-[#B91C1C]" data-testid="reset-error">{error}</p>}
          <button type="submit" className="btn-primary w-full" disabled={busy || pw.length < 8 || pw !== confirm} data-testid="reset-submit-button">{busy ? "Saving…" : "Set new password"}</button>
        </form>
      )}
    </Shell>
  );
}
